import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Check from 'lucide-react/dist/esm/icons/check.js';
import FileText from 'lucide-react/dist/esm/icons/file-text.js';
import Link2 from 'lucide-react/dist/esm/icons/link-2.js';
import Search from 'lucide-react/dist/esm/icons/search.js';
import X from 'lucide-react/dist/esm/icons/x.js';
import { useOptionalContent } from '../contexts/ContentContext';
import { useTranslations } from '../contexts/TranslationContext';
import { createContentReferenceToken } from '../utils/contentReferences';

function stripHtml(value) {
  return String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export default function ContentPickerModal({ currentKey, onSelect, onClose }) {
  const contentContext = useOptionalContent();
  const { t } = useTranslations();
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const label = (key, fallback) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const options = useMemo(() => {
    if (!contentContext?.getContentOptions) return [];
    return contentContext.getContentOptions({ excludeKey: currentKey });
  }, [contentContext, currentKey]);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return options;

    return options.filter(option => {
      const haystack = `${option.key} ${stripHtml(option.value)}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [options, search]);

  const selectedOption = options.find(option => option.key === selectedKey);

  const handleInsert = () => {
    if (!selectedKey) return;
    onSelect(selectedKey);
    onClose();
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-[860px] max-w-[96vw] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-emerald-500 flex flex-col overflow-hidden"
        style={{ height: '78vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 flex-shrink-0">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {label('contentPicker.title', 'Insert Content Reference')}
          </h3>
          <button onClick={onClose} className="text-emerald-100 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/20" type="button">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={label('contentPicker.searchPlaceholder', 'Search content keys or text...')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!contentContext ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              {label('contentPicker.unavailable', 'Content references are only available inside the site content editor.')}
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              {label('contentPicker.noMatches', 'No matching content found.')}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredOptions.map(option => {
                const isSelected = option.key === selectedKey;
                const preview = stripHtml(option.value);
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSelectedKey(option.key)}
                    onDoubleClick={() => {
                      onSelect(option.key);
                      onClose();
                    }}
                    className={`w-full text-left rounded-xl border p-3 transition-all ${
                      isSelected
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 shadow-sm'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-emerald-300 dark:hover:border-emerald-600'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-emerald-700 dark:text-emerald-300 break-all">
                            {option.key}
                          </span>
                          {option.isContentBlock && (
                            <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-semibold uppercase tracking-wide">
                              {label('contentPicker.contentBlock', 'Content Block')}
                            </span>
                          )}
                          <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 text-[10px] font-medium">
                            {option.source}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-200 line-clamp-2">
                          {preview || label('contentPicker.empty', '(empty)')}
                        </p>
                        <div className="mt-2 flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                          <Link2 className="w-3 h-3" />
                          {createContentReferenceToken(option.key)}
                        </div>
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {selectedOption
              ? `${label('contentPicker.selected', 'Selected')}: ${createContentReferenceToken(selectedOption.key)}`
              : label('contentPicker.selectPrompt', 'Select content to insert a live synced reference.')}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors"
              type="button"
            >
              {label('admin.common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleInsert}
              disabled={!selectedKey}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              {label('contentPicker.insertReference', 'Insert Reference')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

